/* eslint-disable @typescript-eslint/no-namespace */
import type { Utility } from '../types/utility';

export namespace Typography {
  /** Typography Hero Styles */
  export namespace Hero {
    /** Heading Hero Variant Keys */
    export type VariantKeys =
      | 'heroXL700'
      | 'heroXL900'
      | 'heroXL900Italic'
      | 'hero2XL700'
      | 'hero2XL900'
      | 'hero2XL900Italic'
      | 'hero3XL400'
      | 'hero3XL700'
      | 'hero3XL900'
      | 'hero3XL900Italic'
      | 'hero4XL400'
      | 'hero4XL700'
      | 'hero4XL900'
      | 'hero4XL900Italic'
      | 'hero5XL700'
      | 'hero5XL900'
      | 'hero5XL900Italic'
      | 'hero6XL700'
      | 'hero6XL900'
      | 'hero6XL900Italic'
      | 'hero7XL700'
      | 'hero7XL900'
      | 'hero7XL900Italic';

    /** Heading Hero Variants */
    export type Variants = {
      [Key in VariantKeys]: Utility.TypographyObject;
    };
  }

  /** Typography Heading Styles */
  export namespace Heading {
    /** Heading Heading Variant Keys */
    export type VariantKeys =
      | 'headingS'
      | 'headingM'
      | 'headingL'
      | 'headingXL'
      | 'heading2XL'
      | 'heading3XL700'
      | 'heading3XL900'
      | 'heading3XL900Italic';

    /** Heading Heading Variants */
    export type Variants = {
      [Key in VariantKeys]: Utility.TypographyObject;
    };
  }

  /** Typography Paragraph Styles */
  export namespace Paragraph {
    /** Typography Paragraph Variant Keys */
    export type VariantKeys =
      | 'paragraph2XL300'
      | 'paragraphXL300'
      | 'paragraphL300'
      | 'paragraphM300'
      | 'paragraphS300'
      | 'paragraph2XL400'
      | 'paragraphXL400'
      | 'paragraphL400'
      | 'paragraphM400'
      | 'paragraphS400'
      | 'paragraphXS400';

    /** Typography Paragraph Variants */
    export type Variants = Utility.TypographyVariantObjects<VariantKeys, Utility.TypographyObject>;
  }

  /** Typography Label Styles */
  export namespace Label {
    /** Typography Label Variant Keys */
    export type VariantKeys =
      | 'labelS700'
      | 'labelS600'
      | 'labelS500'
      | 'labelS400'
      | 'labelXS400'
      | 'labelXS500'
      | 'label2XS400'
      | 'label3XS400';

    /** Typography Label Variants */
    export type Variants = Utility.TypographyVariantObjects<VariantKeys, Utility.TypographyObject>;
  }

  /** Typography Interactive Styles */
  export namespace Interactive {
    /** Typography Interactive Variant Keys */
    export type VariantKeys =
      | 'interactiveL400'
      | 'interactiveM400'
      | 'interactiveS400'
      | 'interactiveXS400'
      | 'interactiveL500'
      | 'interactiveM500'
      | 'interactiveS500'
      | 'interactiveXS500'
      | 'interactiveL700'
      | 'interactiveM700'
      | 'interactiveS700'
      | 'interactiveXS700';

    /** Typography Interactive Variants */
    export type Variants = Utility.TypographyVariantObjects<VariantKeys, Utility.TypographyObject>;
  }

  /** Typography Variant Keys */
  export type VariantKeys =
    | Hero.VariantKeys
    | Heading.VariantKeys
    | Paragraph.VariantKeys
    | Label.VariantKeys
    | Interactive.VariantKeys;

  /** Typography Variants */
  export type Variants = {
    [K in VariantKeys]: Utility.TypographyObject;
  };
}

export const heroStyles: Typography.Hero.Variants = {
  heroXL700: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs32',
    fontWeight: 'fw700',
    lineHeight: 'lh1',
    marginTop: 0,
    marginBottom: '20px',
  },
  heroXL900: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs32',
    fontWeight: 'fw900',
    lineHeight: 'lh1',
    marginTop: 0,
    marginBottom: '20px',
  },
  heroXL900Italic: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs32',
    fontWeight: 'fw900',
    lineHeight: 'lh1',
    marginTop: 0,
    marginBottom: '20px',
  },
  hero2XL700: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: ['fs32', 'fs32', 'fs40'],
    fontWeight: 'fw700',
    lineHeight: 'lh1',
    marginTop: 0,
    marginBottom: 's40',
  },
  hero2XL900: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: ['fs32', 'fs32', 'fs40'],
    fontWeight: 'fw900',
    lineHeight: 'lh1',
    marginTop: 0,
    marginBottom: 's40',
  },
  hero2XL900Italic: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: ['fs32', 'fs32', 'fs40'],
    fontWeight: 'fw900',
    fontStyle: 'italic',
    lineHeight: 'lh1',
    marginTop: 0,
    marginBottom: 's40',
  },
  hero3XL400: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: ['fs32', 'fs32', 'fs48'],
    fontWeight: 'fw400',
    lineHeight: 'lh1',
    marginTop: 0,
    marginBottom: 's40',
  },
  hero3XL700: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: ['fs32', 'fs32', 'fs48'],
    fontWeight: 'fw700',
    lineHeight: 'lh1',
    marginTop: 0,
    marginBottom: 's40',
  },
  hero3XL900: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: ['fs32', 'fs32', 'fs48'],
    fontWeight: 'fw900',
    lineHeight: 'lh1',
    marginTop: 0,
    marginBottom: 's40',
  },
  hero3XL900Italic: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: ['fs32', 'fs32', 'fs48'],
    fontWeight: 'fw900',
    fontStyle: 'italic',
    lineHeight: 'lh1',
    marginTop: 0,
    marginBottom: 's40',
  },
  hero4XL400: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: ['fs40', 'fs40', 'fs40', 'fs40', 'fs48', 'fs64'],
    fontWeight: 'fw400',
    lineHeight: 'lh1',
    marginTop: 0,
    marginBottom: 's40',
  },
  hero4XL700: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: ['fs32', 'fs32', 'fs64'],
    fontWeight: 'fw700',
    lineHeight: 'lh1',
    marginTop: 0,
    marginBottom: 's40',
  },
  hero4XL900: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: ['fs32', 'fs32', 'fs64'],
    fontWeight: 'fw900',
    lineHeight: 'lh1',
    marginTop: 0,
    marginBottom: 's40',
  },
  hero4XL900Italic: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: ['fs32', 'fs32', 'fs64'],
    fontWeight: 'fw900',
    fontStyle: 'italic',
    lineHeight: 'lh1',
    marginTop: 0,
    marginBottom: 's40',
  },
  hero5XL700: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: ['fs32', 'fs32', 'fs80'],
    fontWeight: 'fw700',
    lineHeight: 'lh1',
    marginTop: 0,
    marginBottom: 's40',
  },
  hero5XL900: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: ['fs32', 'fs32', 'fs40', 'fs64', 'fs80'],
    fontWeight: 'fw900',
    lineHeight: 'lh1',
    marginTop: 0,
    marginBottom: 's40',
  },
  hero5XL900Italic: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: ['fs32', 'fs32', 'fs80'],
    fontWeight: 'fw900',
    fontStyle: 'italic',
    lineHeight: 'lh1',
    marginTop: 0,
    marginBottom: 's40',
  },
  hero6XL700: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: ['fs32', 'fs32', 'fs100'],
    fontWeight: 'fw700',
    lineHeight: 'lh1',
    marginTop: 0,
    marginBottom: 's40',
  },
  hero6XL900: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: ['fs32', 'fs32', 'fs100'],
    fontWeight: 'fw900',
    lineHeight: 'lh1',
    marginTop: 0,
    marginBottom: 's40',
  },
  hero6XL900Italic: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: ['fs32', 'fs32', 'fs100'],
    fontWeight: 'fw900',
    fontStyle: 'italic',
    lineHeight: 'lh1',
    marginTop: 0,
    marginBottom: 's40',
  },
  hero7XL700: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: ['fs32', 'fs32', 'fs32', 'fs80', 'fs120'],
    fontWeight: 'fw700',
    lineHeight: 'lh1',
    marginTop: 0,
    marginBottom: 's40',
  },
  hero7XL900: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: ['fs32', 'fs32', 'fs120'],
    fontWeight: 'fw900',
    lineHeight: 'lh1',
    marginTop: 0,
    marginBottom: 's40',
  },
  hero7XL900Italic: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: ['fs32', 'fs32', 'fs120'],
    fontWeight: 'fw900',
    fontStyle: 'italic',
    lineHeight: 'lh1',
    marginTop: 0,
    marginBottom: 's40',
  },
};

export const headingStyles: Typography.Heading.Variants = {
  headingS: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs16',
    fontWeight: 'fw700',
    lineHeight: 'lh1',
    marginTop: 0,
    marginBottom: 's16',
  },
  headingM: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs20',
    fontWeight: 'fw700',
    lineHeight: 'lh1',
    marginTop: 0,
    marginBottom: '20px',
  },
  headingL: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs24',
    fontWeight: 'fw700',
    lineHeight: 'lh1',
    marginTop: 0,
    marginBottom: 's24',
  },
  headingXL: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: ['fs24', 'fs24', 'fs24', 'fs24', 'fs32'],
    fontWeight: 'fw700',
    lineHeight: 'lh1',
    marginTop: 0,
    marginBottom: 's32',
  },
  heading2XL: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: ['fs32', 'fs32', 'fs40'],
    fontWeight: 'fw700',
    lineHeight: 'lh1',
    marginTop: 0,
    marginBottom: 's40',
  },
  heading3XL700: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: ['fs32', 'fs48'],
    fontWeight: 'fw700',
    lineHeight: 'lh1',
    marginTop: 0,
    marginBottom: 's40',
  },
  heading3XL900: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: ['fs32', 'fs48'],
    fontWeight: 'fw900',
    lineHeight: 'lh1',
    marginTop: 0,
    marginBottom: 's40',
  },
  heading3XL900Italic: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: ['fs32', 'fs48'],
    fontWeight: 'fw900',
    fontStyle: 'italic',
    lineHeight: 'lh1',
    marginTop: 0,
    marginBottom: 's40',
  },
};

export const paragraphStyles: Typography.Paragraph.Variants = {
  paragraph2XL300: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs40',
    fontWeight: 'fw300',
    lineHeight: 'lh1_35',
    marginTop: 0,
    marginBottom: 's40',
  },
  paragraphXL300: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: ['fs20', 'fs20', 'fs20', 'fs24', 'fs32'],
    fontWeight: 'fw300',
    lineHeight: 'lh1_35',
    marginTop: 0,
    marginBottom: 's40',
  },
  paragraphL300: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs24',
    fontWeight: 'fw300',
    lineHeight: 'lh1_35',
    marginTop: 0,
    marginBottom: 's40',
  },
  paragraphM300: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs20',
    fontWeight: 'fw300',
    lineHeight: 'lh1_35',
    marginTop: 0,
    marginBottom: '20px',
  },
  paragraphS300: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs16',
    fontWeight: 'fw300',
    lineHeight: 'lh1_35',
    marginTop: 0,
    marginBottom: '20px',
  },
  paragraph2XL400: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs40',
    fontWeight: 'fw400',
    lineHeight: 'lh1_35',
    marginTop: 0,
    marginBottom: 's40',
  },
  paragraphXL400: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs32',
    fontWeight: 'fw400',
    lineHeight: 'lh1_35',
    marginTop: 0,
    marginBottom: 's40',
  },
  paragraphL400: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs24',
    fontWeight: 'fw400',
    lineHeight: 'lh1_35',
    marginTop: 0,
    marginBottom: 's40',
  },
  paragraphM400: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs20',
    fontWeight: 'fw400',
    lineHeight: 'lh1_35',
    marginTop: 0,
    marginBottom: '20px',
  },
  paragraphS400: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs16',
    fontWeight: 'fw400',
    lineHeight: 'lh1_35',
    marginTop: 0,
    marginBottom: '20px',
  },
  paragraphXS400: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs12',
    fontWeight: 'fw400',
    lineHeight: 'lh1_35',
    marginTop: 0,
    marginBottom: '16px',
  },
};

export const labelStyles: Typography.Label.Variants = {
  labelS700: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs16',
    fontWeight: 'fw700',
    lineHeight: 'lh1',
    marginTop: 0,
    marginBottom: 's16',
  },
  labelS600: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs16',
    fontWeight: 'fw600',
    lineHeight: 'lh1',
    marginTop: 0,
    marginBottom: 's16',
  },
  labelS400: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs16',
    fontWeight: 'fw400',
    lineHeight: 'lh1_2',
    marginTop: 0,
    marginBottom: 's16',
  },
  labelS500: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs16',
    fontWeight: 'fw500',
    lineHeight: 'lh1_2',
    marginTop: 0,
    marginBottom: 's16',
  },
  labelXS400: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs12',
    fontWeight: 'fw400',
    lineHeight: 'lh1_2',
    marginTop: 0,
    marginBottom: '12px',
  },
  labelXS500: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs12',
    fontWeight: 'fw500',
    lineHeight: 'lh1_2',
    marginTop: 0,
    marginBottom: '12px',
  },
  label2XS400: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs10',
    fontWeight: 'fw400',
    lineHeight: 'lh1_2',
    marginTop: 0,
    marginBottom: '12px',
  },
  label3XS400: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs8',
    fontWeight: 'fw400',
    lineHeight: 'lh1_2',
    marginTop: 0,
    marginBottom: '12px',
  },
};

export const interactiveStyles: Typography.Interactive.Variants = {
  interactiveL400: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs24',
    fontWeight: 'fw400',
    lineHeight: 'lh1_1',
    marginTop: 0,
    marginBottom: 4,
  },
  interactiveM400: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs20',
    fontWeight: 'fw400',
    lineHeight: 'lh1_1',
    marginTop: 0,
    marginBottom: '20px',
  },
  interactiveS400: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs16',
    fontWeight: 'fw400',
    lineHeight: 'lh1_1',
    marginTop: 0,
    marginBottom: 's16',
  },
  interactiveXS400: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs12',
    fontWeight: 'fw400',
    lineHeight: 'lh1_1',
    marginTop: 0,
    marginBottom: '12px',
  },
  interactiveL500: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs24',
    fontWeight: 'fw500',
    lineHeight: 'lh1_1',
    marginTop: 0,
    marginBottom: 4,
  },
  interactiveM500: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs20',
    fontWeight: 'fw500',
    lineHeight: 'lh1_1',
    marginTop: 0,
    marginBottom: '20px',
  },
  interactiveS500: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs16',
    fontWeight: 'fw500',
    lineHeight: 'lh1_1',
    marginTop: 0,
    marginBottom: 's16',
  },
  interactiveXS500: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs12',
    fontWeight: 'fw500',
    lineHeight: 'lh1_1',
    marginTop: 0,
    marginBottom: '12px',
  },
  interactiveL700: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs24',
    fontWeight: 'fw700',
    lineHeight: 'lh1_1',
    marginTop: 0,
    marginBottom: 4,
  },
  interactiveM700: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs20',
    fontWeight: 'fw700',
    lineHeight: 'lh1_1',
    marginTop: 0,
    marginBottom: '20px',
  },
  interactiveS700: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs16',
    fontWeight: 'fw700',
    lineHeight: 'lh1_1',
    marginTop: 0,
    marginBottom: 's16',
  },
  interactiveXS700: {
    fontFamily: 'graphik',
    color: 'heading',
    fontSize: 'fs12',
    fontWeight: 'fw700',
    lineHeight: 'lh1_1',
    marginTop: 0,
    marginBottom: '12px',
  },
};

export const typographyVariants = {
  hero: Object.keys(heroStyles) as Typography.Hero.VariantKeys[],
  heading: Object.keys(headingStyles) as Typography.Heading.VariantKeys[],
  paragraph: Object.keys(paragraphStyles) as Typography.Paragraph.VariantKeys[],
  label: Object.keys(labelStyles) as Typography.Label.VariantKeys[],
  interactive: Object.keys(interactiveStyles) as Typography.Interactive.VariantKeys[],
  all: Object.keys({
    ...heroStyles,
    ...headingStyles,
    ...paragraphStyles,
    ...labelStyles,
    ...interactiveStyles,
  }) as Typography.VariantKeys[],
};

const typography: Typography.Variants = {
  ...heroStyles,
  ...headingStyles,
  ...paragraphStyles,
  ...labelStyles,
  ...interactiveStyles,
};

export default typography;
